import {Dictionary} from "../../page/elements/dictionary";
import {isNotNull, isNull} from "../../common/utils/basics";
import {LanguageProfile, LanguagesService} from "../../common/languages";
import {autoRegister, resolve} from "../../container";
import {ManagingResources} from "../../common/lifetime";
import {Cookies} from "../../common/cookies";
import {DeviceDetectorService} from "../../common/device";
import Highcharts from "highcharts";
import "highcharts/modules/data";
import "highcharts/modules/exporting";
import "highcharts/modules/offline-exporting";
import "highcharts/modules/export-data";
import "highcharts/modules/accessibility";
import "highcharts/modules/treemap";
import "highcharts/modules/variable-pie";
import "highcharts/modules/full-screen";
import "highcharts/highcharts-more";


const ZONE_RANGE_PATTERN = new RegExp(/^(\s*\d+\s*-\s*\d+)$/);
const ZONE_SINGLE_VALUE_PATTERN = new RegExp(/^(\s*\d+\s*)$/);

const CHART_FULLSCREEN_SPACING = 24;
const CONTEXT_BUTTON_SPACING = 36;

@autoRegister()
export class ChartInitService {
    private activeLanguage: LanguageProfile;
    private isLoaded = false;

    public constructor(private device: DeviceDetectorService = resolve(DeviceDetectorService)) {
        const languageService: LanguagesService = resolve(LanguagesService);
        this.activeLanguage = languageService.activeLanguage();
    }

    public run(element: Element): void {
        if (!this.isLoaded) {
            this.initHighchartsConfig(element);
        }
    }

    private initHighchartsConfig(element: Element): void {
        this.addFullscreenIconToRenderer();
        const dictionary = Dictionary.of(element);

        const highchartsOptions: Highcharts.Options = {
            chart: {
                styledMode: true,
                spacingLeft: 0,
                spacingRight: 0,
                spacingTop: CONTEXT_BUTTON_SPACING,
                spacingBottom: 0,
                className: "highcharts-light",
                events: {
                    fullscreenOpen: function (this: Highcharts.Chart, _event: any): void {
                        this.container.classList.add("fullscreen");
                        this.update({chart: {spacingLeft: CHART_FULLSCREEN_SPACING, spacingTop: CHART_FULLSCREEN_SPACING + CONTEXT_BUTTON_SPACING, spacingRight: CHART_FULLSCREEN_SPACING, spacingBottom: CHART_FULLSCREEN_SPACING}});
                    },
                    fullscreenClose: function (this: Highcharts.Chart, _event: any): void {
                        this.container.classList.remove("fullscreen");
                        this.update({chart: {spacingLeft: 0, spacingTop: CONTEXT_BUTTON_SPACING, spacingRight: 0, spacingBottom: 0}});
                    }
                }
            },
            title: {
                align: "center",
                minScale: 1,
                text: undefined
            },
            lang: {
                viewFullscreen: dictionary.translate("MSG_CHARTS_FULLSCREEN"),
                contextButtonTitle: dictionary.translate("MSG_CHARTS_CONTEXT_BUTTON"),
                printChart: dictionary.translate("MSG_CHARTS_PRINT"),
                downloadPNG: dictionary.translate("MSG_CHARTS_DOWNLOAD_AS_PNG"),
                downloadJPEG: dictionary.translate("MSG_CHARTS_DOWNLOAD_AS_JPEG"),
                downloadPDF: dictionary.translate("MSG_CHARTS_DOWNLOAD_AS_PDF"),
                downloadCSV: dictionary.translate("MSG_CHARTS_DOWNLOAD_AS_CSV"),
                decimalPoint: this.activeLanguage.numberDecimalSeparator,
                thousandsSep: this.activeLanguage.numberGroupSeparator
            },
            credits: {
                enabled: false
            },
            exporting: {
                url: "/services/highcharts/" as {},
                buttons: {
                    contextButton: {
                        buttonSpacing: 0,
                        y: -CONTEXT_BUTTON_SPACING,
                        menuItems: ["printChart", "separator", "downloadPNG", "downloadJPEG", "downloadPDF", "separator", "downloadCSV"]
                    }
                }
            },
            plotOptions: {
                series: {
                    dataLabels: {
                        allowOverlap: true
                    }
                },
                pie: {
                    allowPointSelect: true,
                    cursor: "pointer",
                    dataLabels: {
                        format: "{point.name}: {point.y} ({point.percentage:.1f} %)"
                    },
                    showInLegend: true
                }
            },
            legend: {
                itemMarginBottom: 12
            }
        };

        // iOS does not support fullscreen mode
        if (!this.device.isIOS()) {
            highchartsOptions.exporting!.buttons = {
                ...highchartsOptions.exporting!.buttons,
                fullscreenButton: {
                    symbol: "fullscreen",
                    titleKey: "viewFullscreen",
                    className: "highcharts-contextbutton",
                    buttonSpacing: 0,
                    y: -CONTEXT_BUTTON_SPACING,
                    onclick: (event: Event) => {
                        const target = event.target as HTMLElement;
                        const highchartsContainer: EopHighcharts = target.closest(".enbw-highcharts")!;
                        highchartsContainer.toggleDialog();
                    }
                }
            };
        }

        Highcharts.setOptions(highchartsOptions);
        this.isLoaded = true;
    }

    private addFullscreenIconToRenderer(): void {
        // need to use vector icon, since using images does not work with client-side chart exporting
        Highcharts.SVGRenderer.prototype.symbols.fullscreen = (x: number, y: number, w: number, h: number) => [
            // Top left bracket
            "M", x + w * 0.1, y + h * 0.2,
            "L", x + w * 0.1, y + h * 0.5,
            "M", x + w * 0.1, y + h * 0.2,
            "L", x + w * 0.4, y + h * 0.2,
            // Bottom right bracket
            "M", x + w * 0.9, y + h * 0.9,
            "L", x + w * 0.9, y + (h * 0.6),
            "M", x + w * 0.9, y + h * 0.9,
            "L", x + w * 0.6, y + h * 0.9
        ];
    }
}

// Note: Plain custom element because highcharts does not like Lit-comments in the DOM.
export class EopHighcharts extends ManagingResources(HTMLElement) {
    protected chart: Highcharts.Chart;

    public constructor(protected chartInitService: ChartInitService = resolve(ChartInitService), protected cookies: Cookies = resolve(Cookies)) {
        super();
    }

    public connectedCallback(): void {
        this.chartInitService.run(this);
        this.chart = this.initChart();
    }

    public toggleDialog(): void {
        this.chart.fullscreen.toggle();
    }

    protected initChart(): Highcharts.Chart {
        return Highcharts.chart(this, {
            data: {
                table: this.getAttribute("chart-data") ?? undefined
            },
            chart: {
                type: this.getAttribute("chart-type") ?? undefined
            },
            title: {
                text: this.getAttribute("chart-title") ?? undefined
            },
            subtitle: {
                text: this.getAttribute("chart-subtitle") ?? undefined
            },
            xAxis: {
                title: {
                    text: this.getAttribute("chart-x-title") ?? undefined
                },
                allowDecimals: isNull(this.getAttribute("chart-no-x-decimals")),
                type: this.getAttribute("chart-x-axis-type") as Highcharts.AxisTypeValue ?? "linear"
            },
            yAxis: {
                title: {
                    text: this.getAttribute("chart-y-title") ?? undefined
                },
                allowDecimals: isNull(this.getAttribute("chart-no-y-decimals"))
            },
            plotOptions: {
                series: {
                    dataLabels: {
                        enabled: isNotNull(this.getAttribute("chart-data-labels"))
                    },
                    zoneAxis: "x",
                    zones: this.zoneOptions()
                }
            },
            caption: {
                useHTML: true,
                text: this.querySelector(".chart-caption-template")?.innerHTML ?? undefined
            },
            accessibility: {
                screenReaderSection: {
                    //TODO: revert to default when issue is fixed: https://github.com/highcharts/highcharts/issues/22513
                    beforeChartFormat: "<div>{chartTitle}</div><div>{chartSubtitle}</div>"
                }
            }
        });
    }

    private zoneOptions(): Highcharts.SeriesZonesOptionsObject[] {
        const zoneRange = this.getAttribute("chart-gray-zone") ?? undefined;
        if (!zoneRange) {
            return [];
        }

        const zoneSplit = zoneRange.split(",");
        const zones: Highcharts.SeriesZonesOptionsObject[] = [];

        for (const zone of zoneSplit) {
            if (ZONE_RANGE_PATTERN.test(zone)) {
                const range = zone.split("-");
                zones.push({value: parseInt(range[0].trim()), className: "default-zone"});
                zones.push({value: parseInt(range[1].trim()) + 1, className: "gray-zone"});
            } else if (ZONE_SINGLE_VALUE_PATTERN.test(zone)) {
                const singleZoneValue = parseInt(zone.trim());
                zones.push({value: singleZoneValue, className: "default-zone"});
                zones.push({value: singleZoneValue + 1, className: "gray-zone"});
            }
        }
        return zones;
    }
}

export class EopHighchartsExpert extends EopHighcharts {
    protected initChart(): Highcharts.Chart {
        return Highcharts.chart(this, {
            ...{
                chart: {
                    colorCount: 14
                },
                plotOptions: {
                    series: {
                        animation: this.cookies.get("eop-disable-animations") !== "true"
                    }
                },
                caption: {
                    useHTML: true,
                    text: this.querySelector(".chart-caption-template")?.innerHTML ?? undefined
                },
                accessibility: {
                    screenReaderSection: {
                        beforeChartFormat: "<div>{chartTitle}</div><div>{chartSubtitle}</div>"
                    }
                }
            }, ...this.readRawData<Highcharts.Options>("highcharts-config")
        });
    }
}

customElements.define("eop-highcharts", EopHighcharts);
customElements.define("eop-highcharts-expert", EopHighchartsExpert);